import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Warehouse, Truck, RotateCcw, Package, ClipboardList,
  FileText, MapPin, Settings, LayoutList, ArrowLeft, Clock,
  ChevronDown, ChevronRight, ShoppingCart, BarChart3, WifiOff,
  Smartphone, Activity, FlaskConical, ListChecks, Copy, Zap, Shield,
  History, Layers, Database, Eye
} from 'lucide-react';

const OFFLINE_CAPABLE_PATHS = ['/count', '/stocktaking/offline'];

const sections = [
  {
    title: 'Operations',
    icon: ClipboardList,
    color: 'bg-emerald-500',
    description: 'Stock counts, AbbeyZone Count, and offline sessions',
    items: [
      { title: 'Stocktake Sessions', description: 'Create and manage stocktake sessions', icon: ClipboardList, path: '/stocktaking/sessions', color: 'bg-emerald-500' },
      { title: 'Start a new stocktake', description: 'Begin the next stocktake for a site from its last closing stock', icon: ClipboardList, path: '/stocktaking/import/new-stocktake', color: 'bg-emerald-600' },
      { title: 'AbbeyZone Count', description: 'Tablet-optimised counting app', icon: Smartphone, path: '/count', color: 'bg-cyan-600' },
      { title: 'Offline Sessions', description: 'Download and work offline', icon: WifiOff, path: '/stocktaking/offline', color: 'bg-amber-500' },
    ],
  },
  {
    title: 'Import & Conversion',
    icon: History,
    color: 'bg-violet-600',
    description: 'Bring stocktake data into AbbeyZone',
    items: [
      {
        title: 'Import stocktake data',
        description: 'Import closing stock from a PSB file',
        icon: History,
        path: '/stocktaking/import',
        color: 'bg-violet-600',
      },
      {
        // Click-path shortcut: 1-click to "needs review" items — reduces 3-click path to 2.
        // Defaults to needs_review filter; source/site shown on the page header.
        // Visible to stocktake users (admins + auditors) without adminOnly gate.
        title: 'Product Review',
        description: 'Review items that need a decision before import completes',
        icon: Eye,
        path: '/stocktaking/psb-stage1b-mapping',
        color: 'bg-orange-500',
        stocktakeOnly: true,
      },
    ],
  },
  {
    title: 'Reports',
    icon: BarChart3,
    color: 'bg-indigo-500',
    description: 'Costing analysis and movement reports',
    items: [
      { title: 'Stock Audit Analytics', description: 'Movement trail, KPIs & variance analysis', icon: BarChart3, path: '/stocktaking/costing-report', color: 'bg-indigo-500' },
      { title: 'Report Centre', description: 'Summary, Core Audit, Valuation & Supporting reports', icon: FileText, path: '/stocktaking/reports', color: 'bg-fuchsia-500' },
    ],
  },
  {
    title: 'Registers',
    icon: FileText,
    color: 'bg-teal-500',
    description: 'Browse posted transactions and history',
    items: [
      { title: 'Delivery Register', description: 'View all posted deliveries', icon: FileText, path: '/stocktaking/delivery-register', color: 'bg-teal-500' },
      { title: 'Credit Register', description: 'View all posted credit notes', icon: FileText, path: '/stocktaking/credit-register', color: 'bg-indigo-500' },
      { title: 'Stocktake History', description: 'Browse completed sessions', icon: Clock, path: '/stocktaking/session-history', color: 'bg-slate-400', placeholder: true },
    ],
  },
  {
    title: 'Master Data',
    icon: Package,
    color: 'bg-amber-500',
    description: 'Products, suppliers, and report list defaults',
    items: [
      { title: 'Products', description: 'Beverage stock master', icon: Package, path: '/settings/beverage-stock-master', color: 'bg-amber-500' },
      { title: 'Suppliers', description: 'Manage suppliers', icon: Truck, path: '/stocktaking/suppliers', color: 'bg-emerald-600' },
      { title: 'Measures', description: 'Selling measure types (Shot, Glass, Pint...)', icon: LayoutList, path: '/stocktaking/measures', color: 'bg-violet-500' },
      { title: 'Allowances', description: 'Standard allowance names for Summary Audit Reports', icon: ListChecks, path: '/stocktaking/allowances-master', color: 'bg-rose-500' },
      { title: 'Management Items', description: 'Standard management item names for Summary Audit Reports', icon: ListChecks, path: '/stocktaking/management-master', color: 'bg-cyan-600' },
      { title: 'Adjustments', description: 'Standard adjustment names for Summary Audit Reports', icon: ListChecks, path: '/stocktaking/adjustments-master', color: 'bg-purple-500' },
    ],
  },
  {
    title: 'Site Setup',
    icon: Settings,
    color: 'bg-orange-500',
    description: 'Inventory, audit settings, and selling prices',
    items: [
      { title: 'Site Stocktake Settings', description: 'Per-site counting configuration', icon: Settings, path: '/stocktaking/site-settings', color: 'bg-slate-500' },
      { title: 'Site Inventory', description: 'Manage stock items per site', icon: LayoutList, path: '/stocktaking/site-inventory', color: 'bg-orange-500' },
      { title: 'Site Bar Prices', description: 'Selling prices per site / location', icon: ShoppingCart, path: '/stocktaking/site-bar-prices', color: 'bg-rose-500' },
      { title: 'Copy Assigned Products', description: 'Copy product assignments from one site to another', icon: Copy, path: '/stocktaking/copy-assigned-products', color: 'bg-cyan-600', adminOnly: true },
    ],
  },
  {
    title: 'Administration',
    icon: Shield,
    color: 'bg-sky-500',
    description: 'Global module defaults — Admin only',
    items: [
      { title: 'Stocktaking Settings', description: 'Global module defaults (VAT, invoice basis)', icon: Settings, path: '/stocktaking/org-settings', color: 'bg-sky-500' },
      { title: 'Product Categories', description: 'Manage main categories, subcategories, and inventory-code ranges', icon: LayoutList, path: '/stocktaking/product-categories', color: 'bg-sky-600' },
      { title: 'Product Master Audit', description: 'Fix names, codes, measures and category errors across the master', icon: Shield, path: '/stocktaking/product-master-audit', color: 'bg-violet-600', adminOnly: true },
      { title: 'Bulk Measure Assignment', description: 'Set selling measures for multiple products at once', icon: Zap, path: '/stocktaking/bulk-measure-assign', color: 'bg-indigo-600', adminOnly: true },
      { title: 'Recipes & Post-Mix', description: 'Post-mix recipe engine — temporarily retired', icon: FlaskConical, path: '/settings/recipes', color: 'bg-purple-500', disabled: true },
      // Admin-only recovery tools — not part of ordinary workflow
      // 2026-11-03: hidden from this menu before publication (user request — "hide
      // only the three internal PSB links under Administration"). Routes, backend
      // diagnostics, data and the supported Import & Conversion / Product Review
      // journey (same /psb-stage1b-mapping route, reached via the "Product Review"
      // tile above) are all untouched — these three rows are still direct-URL
      // reachable, just no longer advertised in this menu.
      { title: 'Legacy Import Pipeline', description: 'Admin: import pipeline ingestion and diagnostics', icon: Database, path: '/stocktaking/psb-stage1-import', color: 'bg-slate-600', adminOnly: true, hiddenFromMenu: true },
      { title: 'Legacy Product Mapping', description: 'Admin: product mapping and cascade diagnostics', icon: Layers, path: '/stocktaking/psb-stage1b-mapping', color: 'bg-slate-600', adminOnly: true, hiddenFromMenu: true },
      { title: 'Legacy Reconciliation Review', description: 'Admin: calculation and reconciliation diagnostics', icon: BarChart3, path: '/stocktaking/psb-stage7-calc', color: 'bg-slate-600', adminOnly: true, hiddenFromMenu: true },
    ],
  },
];

export default function StocktakingDashboardPage() {
  const navigate = useNavigate();
  const { isAdmin, isAuditor } = useAuth();
  const [expandedSections, setExpandedSections] = useState({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useState(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);

  const toggleSection = (title) => {
    setExpandedSections(prev => ({ ...prev, [title]: !prev[title] }));
  };

  return (
    <div className="space-y-6" data-testid="stocktaking-dashboard">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')}
          data-testid="back-to-home">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Warehouse className="h-6 w-6 text-slate-600" />
            Stocktaking
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage deliveries, credits, stock counts, suppliers, and site operations
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sections.map((section) => {
          const SectionIcon = section.icon;
          const isExpanded = expandedSections[section.title];
          return (
            <div key={section.title} className="col-span-1">
              <Card
                className={`cursor-pointer transition-all duration-200 hover:shadow-md border-2 ${isExpanded ? 'border-slate-300 shadow-md' : 'hover:border-slate-200'}`}
                onClick={() => toggleSection(section.title)}
                data-testid={`section-tile-${section.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`${section.color} w-10 h-10 rounded-lg flex items-center justify-center`}>
                        <SectionIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{section.title}</CardTitle>
                        <CardDescription className="text-xs">{section.description}</CardDescription>
                      </div>
                    </div>
                    {isExpanded
                      ? <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      : <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    }
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent className="pt-0 pb-3">
                    <div className="space-y-1 mt-2 border-t pt-3">
                      {section.items
                        .filter(item => {
                          if (item.hiddenFromMenu)                     return false;
                          if (item.adminOnly  && !isAdmin)             return false;
                          if (item.stocktakeOnly && !isAdmin && !isAuditor) return false;
                          return true;
                        })
                        .map((item) => {
                        const ItemIcon = item.icon;
                        const offlineBlocked = !isOnline && !OFFLINE_CAPABLE_PATHS.includes(item.path);
                        return (
                          <Link
                            key={item.path}
                            to={(offlineBlocked || item.disabled) ? '#' : item.path}
                            onClick={e => { e.stopPropagation(); if (offlineBlocked || item.disabled) e.preventDefault(); }}
                            data-testid={`stocktaking-tile-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
                              offlineBlocked
                                ? 'opacity-40 cursor-not-allowed'
                                : item.disabled ? 'opacity-40 cursor-not-allowed' : item.placeholder ? 'opacity-50 hover:bg-slate-50' : 'hover:bg-slate-50'
                            }`}
                          >
                            <div className={`${item.color} w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0`}>
                              <ItemIcon className="h-4 w-4 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-800 truncate flex items-center gap-1">
                                {item.title}
                                {item.adminOnly && <Shield className="h-3 w-3 text-slate-400 flex-shrink-0" />}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {item.description}
                                {item.placeholder && <span className="italic ml-1">(Coming soon)</span>}
                                {item.disabled && <span className="italic ml-1 text-amber-600">(Retired)</span>}
                              </p>
                            </div>
                            {offlineBlocked && <WifiOff className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                          </Link>
                        );
                      })}
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>
          );
        })}
      </div>
    </div>
  );
}
